import { useRef } from 'react';
import { Button, Modal, buttonClassName } from '@safetyhub/ui';

interface CompanyReverifyConfirmModalProps {
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function CompanyReverifyConfirmModal({
  isOpen,
  isPending,
  onClose,
  onConfirm,
}: CompanyReverifyConfirmModalProps) {
  // danger 규약 — 첫 포커스는 「취소」(Enter 오조작으로 되돌릴 수 없는 재검증이 나가지 않게).
  // `Button`은 ref를 넘기지 않아(forwardRef 없음) 취소만 같은 정본 클래스의 네이티브 버튼으로 둔다.
  const cancelRef = useRef<HTMLButtonElement>(null);

  // 처리 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전 `preventClose`와 같은 축.
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="사업자 정보 재검증"
      size="md"
      initialFocusRef={cancelRef}
      closeOnOverlay={!isPending}
      closeOnEsc={!isPending}
      showCloseButton={!isPending}
      closeLocked={isPending}
      footer={
        <>
          <button
            ref={cancelRef}
            type="button"
            className={buttonClassName('secondary', 'sm')}
            onClick={onClose}
            disabled={isPending}
          >
            취소
          </button>
          {/* "되돌릴 수 없습니다" 안내가 붙는 파괴적 확정이라 danger 유지. */}
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending && (
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
            )}
            재검증 실행
          </Button>
        </>
      }
    >
      <div className="text-sm text-gray-700 space-y-2">
        <p>
          국세청 <strong>/validate</strong>로 사업자 정보를 재검증합니다.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 leading-relaxed">
          재검증이 성공하면 <strong>검증 완료(FULLY_VERIFIED)</strong>로 승격되고
          국세청 정보(상호·상태)가 갱신됩니다.
          <br />
          다른 조직의 Company 레코드에는 영향을 주지 않습니다. 중복 레코드 정리는
          해당 조직의 회사 인증(클레임) 시점에 수행됩니다.
        </div>
        <p className="text-xs text-gray-500">
          이 작업은 감사 로그에 기록되며 되돌릴 수 없습니다.
        </p>
      </div>
    </Modal>
  );
}
